/**
 * gift-wrapping.js
 * ---------------------------------------------------------------
 * Handles the "Gift Wrapping" checkbox in the cart drawer:
 *  - Adds/removes the Gift Wrapping product as its own line item
 *  - Shows/hides the gift message field
 *  - Writes the message (or blank) as a line item property
 *    (this is what shows at checkout under the Gift Wrapping line)
 *  - ALSO writes the message into the cart note, which becomes the
 *    "Notes" box on the Shopify admin order (message text only, no prefix)
 *  - Opens/closes the info modal
 *
 * Include after your theme's cart.js / ajax-cart logic loads.
 * Requires the markup from snippet-gift-wrapping.liquid.
 */

(function () {
  // ---------------------------------------------------------------
  // This theme compiles the cart drawer from a <script type="text/
  // template"> at runtime, so the gift-wrapping markup does NOT exist
  // in the DOM at page load, and gets rebuilt on every cart update.
  // Everything here uses event delegation on `document` for that reason.
  // ---------------------------------------------------------------

  // Since the note no longer has a "Gift Message:" prefix, we remember the
  // exact line we wrote in a hidden cart attribute. That lets us find and
  // replace/remove ONLY our line later, without touching other note text.
  var GIFT_NOTE_ATTR = '_giftwrap_note';

  function getVariantId() {
    var block = document.querySelector('.gift-wrapping-block');
    return block ? block.dataset.giftWrapVariantId : null;
  }

  function refreshSubtotal() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (typeof window.updateCartSubtotal === 'function') {
          window.updateCartSubtotal(cart, cart.items);
        }
      })
      .catch(function (err) { console.error('Gift wrap: failed to refresh subtotal', err); });
  }

  // ---------------------------------------------------------------
  // Save queue
  // ---------------------------------------------------------------
  // Every save (line item property + cart note) runs one after another,
  // in the order the customer made the changes. Without this, two saves
  // fired a moment apart can finish out of order, and an OLDER, shorter
  // version of the message ends up in the order note (this is why the
  // note could show a cut-off message while the line item showed it in full).
  var giftSaveQueue = Promise.resolve();
  function enqueueGiftSave(task) {
    giftSaveQueue = giftSaveQueue
      .then(task)
      .catch(function (err) { console.error('Gift wrap: save failed', err); });
    return giftSaveQueue;
  }

  // ---------------------------------------------------------------
  // Cart note helpers
  // ---------------------------------------------------------------

  // Raw writer. Only call from inside the queue (or via setGiftNote).
  function writeGiftNote(message) {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var attrs = cart.attributes || {};
        var previous = attrs[GIFT_NOTE_ATTR] || '';

        // Keep every note line except the one we wrote last time.
        var lines = (cart.note || '')
          .split('\n')
          .filter(function (l) { return !previous || l !== previous; });

        var clean = (message || '').replace(/\s+/g, ' ').trim();
        if (clean) lines.push(clean);
        var newNote = lines.join('\n');

        // Skip the request if nothing actually changed.
        if (newNote === (cart.note || '') && clean === previous) return null;

        var newAttrs = {};
        newAttrs[GIFT_NOTE_ATTR] = clean; // empty string clears the attribute

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: newNote, attributes: newAttrs })
        });
      });
  }

  function setGiftNote(message) {
    return enqueueGiftSave(function () { return writeGiftNote(message); });
  }

  // ---------------------------------------------------------------
  // Cart actions
  // ---------------------------------------------------------------

  async function addGiftWrap(variantId) {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        // '_giftwrap_leave_blank' starts explicitly 'false' so a freshly
        // added gift wrap starts with "Leave as Blank" unchecked and the
        // message field enabled.
        items: [{ id: variantId, quantity: 1, properties: { 'Gift Message': '', '_giftwrap_leave_blank': 'false' } }]
      })
    });
    refreshSubtotal();
  }

  async function removeGiftWrap(variantId) {
    await enqueueGiftSave(async function () {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 })
      });
      // Gift wrap is gone, so the gift message must not stay in the order note.
      await writeGiftNote('');
    });
    refreshSubtotal();
  }

  // leaveBlank is persisted as its own property, separate from whether
  // the message text itself happens to be empty.
  function updateGiftMessage(variantId, message, leaveBlank) {
    return enqueueGiftSave(async function () {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { 'Gift Message': message, '_giftwrap_leave_blank': leaveBlank ? 'true' : 'false' }
        })
      });
      // Mirror the message into the cart note -> order "Notes" in admin.
      await writeGiftNote(leaveBlank ? '' : message);
    });
  }

  // ---- Checkbox toggle: add / remove the line item ----
  document.addEventListener('change', function (e) {
    if (e.target.id === 'GiftWrappingCheckbox') {
      var variantId = getVariantId();
      var messageWrap = document.getElementById('GiftWrappingMessageWrap');
      if (e.target.checked) {
        addGiftWrap(variantId);
        if (messageWrap) messageWrap.hidden = false;
      } else {
        removeGiftWrap(variantId);
        if (messageWrap) messageWrap.hidden = true;
      }
    }

    if (e.target.id === 'GiftWrappingLeaveBlank') {
      var messageField = document.getElementById('GiftWrappingMessage');
      var vId = getVariantId();
      if (messageField) {
        markGiftWrapUserInteraction();
        messageField.disabled = e.target.checked;
        if (e.target.checked) messageField.value = '';
        updateGiftMessage(vId, messageField.value, e.target.checked);
      }
    }
  });

  // ---- Save message as you type (debounced) ----
  var debounceTimer;
  document.addEventListener('input', function (e) {
    if (e.target.id === 'GiftWrappingMessage') {
      markGiftWrapUserInteraction();
      var vId = getVariantId();
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        updateGiftMessage(vId, e.target.value, false);
      }, 500);
    }
  });

  // ---------------------------------------------------------------
  // Keep the checkbox / message field in sync with the REAL cart state.
  // ---------------------------------------------------------------
  // ---------------------------------------------------------------
  // Mobile only: gift wrapping ticked + message box open by default
  // ---------------------------------------------------------------
  // On mobile, once per session, Gift Wrapping is added automatically
  // when the drawer opens with a product in the cart. If the customer
  // unticks it, it stays off (we don't re-add it). Desktop is untouched.
  // Change this to match your theme's mobile breakpoint.
  var GIFT_WRAP_MOBILE_QUERY = '(max-width: 749px)';
  var GIFT_WRAP_AUTO_FLAG = 'tpsGiftWrapAutoAdded';

  function isMobileView() {
    return !!(window.matchMedia && window.matchMedia(GIFT_WRAP_MOBILE_QUERY).matches);
  }
  function getAutoFlag() {
    try { return sessionStorage.getItem(GIFT_WRAP_AUTO_FLAG) === '1'; } catch (e) { return false; }
  }
  function setAutoFlag(on) {
    try {
      if (on) sessionStorage.setItem(GIFT_WRAP_AUTO_FLAG, '1');
      else sessionStorage.removeItem(GIFT_WRAP_AUTO_FLAG);
    } catch (e) {}
  }

  var giftWrapSyncInProgress = false;
  var giftWrapSyncQueued = false;
  var giftNoteCleanupInProgress = false;

  // Cooldown after a direct user action so the background sync doesn't
  // revert what the person just typed/toggled before it finished saving.
  var GIFT_WRAP_INTERACTION_COOLDOWN_MS = 1000;
  var lastGiftWrapUserInteraction = 0;
  function markGiftWrapUserInteraction() {
    lastGiftWrapUserInteraction = Date.now();
  }

  function syncGiftWrapUI() {
    if (giftWrapSyncInProgress) {
      giftWrapSyncQueued = true;
      return;
    }
    giftWrapSyncInProgress = true;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var giftWrapItem = null;
        (cart.items || []).forEach(function (item) {
          if (item.title === 'Gift Wrapping') giftWrapItem = item;
        });

        // If Gift Wrapping was removed some other way (e.g. the line
        // item's own "Remove" button), clear our line from the note.
        var hasOurNoteLine = !!((cart.attributes || {})[GIFT_NOTE_ATTR]);
        if (!giftWrapItem && hasOurNoteLine && !giftNoteCleanupInProgress) {
          giftNoteCleanupInProgress = true;
          setGiftNote('').then(function () { giftNoteCleanupInProgress = false; });
        }

        // Mobile only: auto-add Gift Wrapping once per session.
        var hasProduct = (cart.items || []).some(function (i) { return i.title !== 'Gift Wrapping'; });
        if (!hasProduct) setAutoFlag(false); // empty cart -> allow again next time
        if (isMobileView() && !giftWrapItem && hasProduct && !getAutoFlag()) {
          var autoVariantId = getVariantId(); // only exists once the drawer is rendered
          if (autoVariantId) {
            setAutoFlag(true);
            addGiftWrap(autoVariantId);
          }
        }

        var checkbox = document.getElementById('GiftWrappingCheckbox');
        var messageWrap = document.getElementById('GiftWrappingMessageWrap');
        var messageField = document.getElementById('GiftWrappingMessage');
        var leaveBlank = document.getElementById('GiftWrappingLeaveBlank');
        var withinCooldown = (Date.now() - lastGiftWrapUserInteraction) < GIFT_WRAP_INTERACTION_COOLDOWN_MS;

        if (checkbox) checkbox.checked = !!giftWrapItem;
        if (messageWrap) messageWrap.hidden = !giftWrapItem;

        if (giftWrapItem && messageField && document.activeElement !== messageField && !withinCooldown) {
          var props = giftWrapItem.properties || {};
          var msg = props['Gift Message'] || '';
          var isLeaveBlank = props['_giftwrap_leave_blank'] === 'true';

          messageField.value = msg;
          messageField.disabled = isLeaveBlank;
          if (leaveBlank && document.activeElement !== leaveBlank) {
            leaveBlank.checked = isLeaveBlank;
          }
        }
      })
      .catch(function (err) { console.error('Gift wrap: failed to sync UI', err); })
      .finally(function () {
        giftWrapSyncInProgress = false;
        if (giftWrapSyncQueued) {
          giftWrapSyncQueued = false;
          syncGiftWrapUI();
        }
      });
  }

  var giftWrapSyncDebounce;
  function scheduleGiftWrapSync() {
    clearTimeout(giftWrapSyncDebounce);
    giftWrapSyncDebounce = setTimeout(syncGiftWrapUI, 150);
  }

  function startGiftWrapSyncObserver() {
    scheduleGiftWrapSync();
    var observer = new MutationObserver(scheduleGiftWrapSync);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.body) {
    startGiftWrapSyncObserver();
  } else {
    document.addEventListener('DOMContentLoaded', startGiftWrapSyncObserver);
  }

  // ---- Info modal open/close ----
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.gift-wrapping-block__info-trigger')) {
      var modal = document.getElementById('GiftWrappingModal');
      if (modal) modal.hidden = false;
    }
    if (e.target.classList && e.target.classList.contains('gift-wrapping-modal__close')) {
      var modalToClose = document.getElementById('GiftWrappingModal');
      if (modalToClose) modalToClose.hidden = true;
    }
    if (e.target.id === 'GiftWrappingModal') {
      e.target.hidden = true; // click on backdrop
    }
  });
})();